import React, { useRef, useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ProfileColumnDef } from './types'
import { Popover } from '../Popover'
import { SortIcon } from 'components/shared/SortIcon'
import { Checkbox } from '../Checkbox'
import { PROFILE_TABLE_VARIANT_STYLES } from './variantStyles'

interface ProfileTableHeaderCellProps<TKey extends string> {
    uiVariant?: keyof typeof PROFILE_TABLE_VARIANT_STYLES
    column: ProfileColumnDef<TKey>
    collapsed: boolean
    isMiddleColumn: boolean
    baseStyle: React.CSSProperties
    addableColumns: Array<{ key: string; label: string }>
    hideDisabled: boolean
    onToggleCollapse: () => void
    onAddToRight: (columnKey: string) => void
    onHide: () => void
    allSelected: boolean
    someSelected: boolean
    onSelectAll?: (checked: boolean) => void
    onResizeStart: (event: React.MouseEvent) => void
    isResizing: boolean
    hoverAccent: string
    showSortTrigger: boolean
    sortDirection: 'asc' | 'desc' | null
    onSortTrigger?: () => void
    /** Header text beside the sort trigger on the selection column, which otherwise shows only its checkbox. */
    sortLabel?: string
    /** Distance from the pinned edge for a sticky fixed column (0 for the first one on its side). */
    stickyOffset?: number
    accentColor: string
    selectionColumnKey?: string
}

// Kebab / three-dot vertical — opens the per-column menu (Expand/Collapse, Hide, Show X Column).
// Mirrors the scheduler DayView's `HeaderColumnMenu` so every DS table exposes the same
// per-column affordances the user is already familiar with.
const KebabIcon = () => (
    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <circle cx="8" cy="3.5" r="1.4" />
        <circle cx="8" cy="8" r="1.4" />
        <circle cx="8" cy="12.5" r="1.4" />
    </svg>
)

// Previous `< >` chevron collapse toggle was removed: collapsing is now driven
// exclusively through the kebab menu's "Expand / Collapse" item to match the
// user's request ("hamma joydan collapse qilish vertical 3ta nuqtadan chiqsin
// eski <> iconlarni olib tashla"). A single affordance per action keeps the
// header less noisy and the interaction discoverable.

export const ProfileTableHeaderCell = <TKey extends string>({
    uiVariant = 'default',
    column,
    collapsed,
    isMiddleColumn,
    baseStyle,
    addableColumns,
    hideDisabled,
    onToggleCollapse,
    onAddToRight,
    onHide,
    allSelected,
    someSelected,
    onSelectAll,
    onResizeStart,
    isResizing,
    hoverAccent,
    showSortTrigger,
    sortDirection,
    onSortTrigger,
    sortLabel,
    stickyOffset = 0,
    selectionColumnKey = 'selection',
}: ProfileTableHeaderCellProps<TKey>) => {
    const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
        id: column.key,
        disabled: !column.draggable || !isMiddleColumn,
    })

    // Per-column kebab menu state — opens on click, closes on outside-click or Escape.
    // Only rendered for middle columns that have at least one actionable item.
    const kebabRef = useRef<HTMLButtonElement | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const hasMenu = isMiddleColumn && !!column.showMenu
    const canToggleCollapse = hasMenu && !!column.canCollapse
    const canHide = hasMenu && !!column.canHide && !hideDisabled
    const hasAddable = hasMenu && addableColumns.length > 0
    const menuHasItems = canToggleCollapse || canHide || hasAddable

    // Sticky positioning is only applied to fixed columns (middle columns
    // participate in DnD and would fight the drag transform). The bg stays
    // opaque so the pinned cell cleanly covers rows scrolling underneath.
    const stickyStyle: React.CSSProperties = !isMiddleColumn && column.sticky ? { position: 'sticky', [column.sticky]: stickyOffset, zIndex: 15 } : {}

    const style: React.CSSProperties = {
        ...baseStyle,
        ...stickyStyle,
        transform: CSS.Transform.toString(transform),
        transition: transition ?? 'transform 180ms cubic-bezier(0.2, 0, 0, 1)',
    }
    // Scheduler → Dispatch Board's Day View uses a 16px horizontal header gutter.
    const variantStyles = PROFILE_TABLE_VARIANT_STYLES[uiVariant]

    const isSelectionColumn = column.key === selectionColumnKey
    const sortButton = showSortTrigger ? (
        <button
            type="button"
            aria-label={`Sort ${sortLabel ?? column.label} ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
            title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
            onClick={(e) => {
                e.stopPropagation()
                onSortTrigger?.()
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="shrink-0"
        >
            <SortIcon direction={sortDirection} active={!!sortDirection} uiVariant={uiVariant} />
        </button>
    ) : null
    const cursorClass = column.draggable && isMiddleColumn ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'
    return (
        <th
            ref={setNodeRef}
            style={variantStyles.headerStyle(style, collapsed)}
            // Tailwind is imported `important`, so the cell's `relative` class would beat the inline `position: sticky`
            // and leave a pinned header shifted by its `left` offset instead of pinned. Swap it for `sticky`.
            className={stickyStyle.position ? variantStyles.headerCell.replace(/\brelative\b/, 'sticky') : variantStyles.headerCell}
        >
            <div
                {...attributes}
                {...listeners}
                className={`flex items-center ${collapsed ? 'justify-center' : ''} gap-1 w-full min-w-0 ${collapsed || !isMiddleColumn ? 'pr-0' : 'pr-2.5'} ${cursorClass} touch-none`}
            >
                {isSelectionColumn ? (
                    // Only render the select-all checkbox when the consumer wires up
                    // `onSelectAll`. Tables that manage selection via a custom toolbar
                    // (e.g. InvoiceTable's TONU-aware select-all) omit the prop so this
                    // built-in checkbox doesn't render as a dead/duplicate control.
                    <>
                        {onSelectAll ? (
                            <div className={`flex justify-start ${sortLabel ? '' : 'w-full'}`} onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onChange={onSelectAll}
                                    onClick={(e) => e.stopPropagation()}
                                    className="m-0"
                                />
                            </div>
                        ) : sortLabel ? null : (
                            <div className="w-full" />
                        )}
                        {sortLabel && (
                            <>
                                <span title={sortLabel} className={variantStyles.headerLabel} style={{ textAlign: column.align ?? 'left' }}>
                                    {sortLabel}
                                </span>
                                {sortButton}
                            </>
                        )}
                    </>
                ) : collapsed ? (
                    // Collapsed-state affordance: the whole 36px column is clickable and
                    // shows the kebab glyph, so "expand" joins the single-affordance pattern
                    // used by all other per-column actions (see the kebab menu below). No
                    // more `<>` chevron pair anywhere in the header.
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            onToggleCollapse()
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="flex h-full w-full items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                        title={`Expand "${column.label}"`}
                    >
                        <KebabIcon />
                    </button>
                ) : column.label ? (
                    <>
                        <span title={column.label} className={variantStyles.headerLabel} style={{ textAlign: column.align ?? 'left' }}>
                            {column.label}
                        </span>
                        {sortButton}
                        {hasMenu && menuHasItems && (
                            <>
                                <button
                                    ref={kebabRef}
                                    type="button"
                                    aria-label="Column options"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setMenuOpen((prev) => !prev)
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    className={variantStyles.kebabButton}
                                >
                                    <KebabIcon />
                                </button>
                                <Popover open={menuOpen} onClose={() => setMenuOpen(false)} anchorEl={kebabRef.current} placement="bottom-end">
                                    <div className={variantStyles.popover}>
                                        {canToggleCollapse && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMenuOpen(false)
                                                    onToggleCollapse()
                                                }}
                                                className={variantStyles.menuItem}
                                            >
                                                {collapsed ? 'Expand column' : 'Collapse column'}
                                            </button>
                                        )}
                                        {canHide && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMenuOpen(false)
                                                    onHide()
                                                }}
                                                className={variantStyles.menuItem}
                                            >
                                                Hide column
                                            </button>
                                        )}
                                        {hasAddable && (canToggleCollapse || canHide) && <div className={variantStyles.menuSeparator} />}
                                        {addableColumns.map((hidden) => (
                                            <button
                                                key={hidden.key}
                                                type="button"
                                                onClick={() => {
                                                    setMenuOpen(false)
                                                    onAddToRight(hidden.key)
                                                }}
                                                className={variantStyles.menuItem}
                                            >
                                                Show {hidden.label}
                                            </button>
                                        ))}
                                    </div>
                                </Popover>
                            </>
                        )}
                    </>
                ) : (
                    <div className="w-full" />
                )}
            </div>

            {(() => {
                const canResize = typeof onResizeStart === 'function' && (column.maxWidth == null || column.maxWidth !== column.minWidth)
                const allowResize = canResize && (!isSelectionColumn || isMiddleColumn)
                if (!allowResize) return null
                return (
                    <div
                        title="Resize Column"
                        onMouseDown={onResizeStart}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="absolute right-0 top-0 bottom-0 z-20 w-1.5 cursor-col-resize transition-colors duration-150"
                        style={{ backgroundColor: isResizing ? hoverAccent : 'transparent' }}
                        onMouseEnter={(e) => {
                            if (!isResizing) (e.currentTarget as HTMLDivElement).style.backgroundColor = hoverAccent
                        }}
                        onMouseLeave={(e) => {
                            if (!isResizing) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'
                        }}
                    />
                )
            })()}
        </th>
    )
}
